import { useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { LINHA_DO_DRE, NATUREZA_LABELS, linhasDoTipo, linhaTemNatureza, type LinhaDoDre, type NaturezaDoCusto, type TipoFinanceiro } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { cn } from "@/lib/cn";
import type { CategoriaFinanceira } from "@/lib/dinheiro";
import { campo, moldura } from "../estilos";

type Rascunho = { id?: string; nome: string; tipo: TipoFinanceiro; natureza: NaturezaDoCusto; linhaDoDre: LinhaDoDre | null; cor: string };

// A linha das perdas é do sistema: só o A receber lança nela.
const LINHAS_ESCOLHIVEIS = (tipo: TipoFinanceiro) => linhasDoTipo(tipo).filter((l) => l !== "PERDAS_COM_CLIENTES");

function Editor({ inicial, comDre, onFechar }: { inicial: Rascunho; comDre: boolean; onFechar: () => void }) {
  const utils = trpc.useUtils();
  const [r, setR] = useState(inicial);
  const salvar = trpc.financeiro.categorias.salvar.useMutation({
    onSuccess: () => {
      toast.success("Categoria salva.");
      utils.financeiro.invalidate();
      onFechar();
    },
    onError: (e) => toast.error(e.message),
  });
  return (
    <div className="flex flex-wrap items-center gap-2 bg-zinc-50/60 px-4 py-3 dark:bg-muted/30">
      <input type="color" value={r.cor} onChange={(e) => setR({ ...r, cor: e.target.value })} aria-label="Cor" className="h-9 w-10 cursor-pointer rounded-md border border-zinc-200 bg-white p-1 dark:border-border dark:bg-card" />
      <input autoFocus value={r.nome} onChange={(e) => setR({ ...r, nome: e.target.value })} placeholder="Nome da categoria" className={cn(campo, "min-w-[180px] flex-1")} />
      {comDre && (
        <select aria-label="Linha do DRE" value={r.linhaDoDre ?? ""} onChange={(e) => setR({ ...r, linhaDoDre: (e.target.value || null) as LinhaDoDre | null })} className={cn(campo, "w-52")}>
          <option value="">Sem linha do DRE</option>
          {LINHAS_ESCOLHIVEIS(r.tipo).map((l) => (
            <option key={l} value={l}>
              {LINHA_DO_DRE[l].rotulo}
            </option>
          ))}
        </select>
      )}
      {r.tipo === "SAIDA" && (!comDre || linhaTemNatureza(r.linhaDoDre)) && (
        <select value={r.natureza} onChange={(e) => setR({ ...r, natureza: e.target.value as NaturezaDoCusto })} className={cn(campo, "w-36")}>
          <option value="FIXO">Custo fixo</option>
          <option value="VARIAVEL">Custo variável</option>
        </select>
      )}
      <button type="button" onClick={onFechar} className="h-9 rounded-md px-3 text-sm text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted">
        Cancelar
      </button>
      <button
        type="button"
        disabled={r.nome.trim().length < 2 || salvar.isPending}
        // No financeiro simples a linha não aparece e não muda.
        onClick={() => salvar.mutate(comDre ? r : { ...r, linhaDoDre: undefined })}
        className="h-9 rounded-md bg-brand-600 px-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
      >
        Salvar
      </button>
      {comDre && r.linhaDoDre && <p className="w-full text-[11px] text-zinc-500 dark:text-muted-foreground">{LINHA_DO_DRE[r.linhaDoDre].dica}</p>}
    </div>
  );
}

function Grupo({ tipo, categorias, pode, comDre }: { tipo: TipoFinanceiro; categorias: CategoriaFinanceira[]; pode: boolean; comDre: boolean }) {
  const utils = trpc.useUtils();
  const [editando, setEditando] = useState<Rascunho | null>(null);
  const alternar = trpc.financeiro.categorias.alternarAtiva.useMutation({ onSuccess: () => utils.financeiro.invalidate() });
  const novo: Rascunho = { nome: "", tipo, natureza: tipo === "SAIDA" ? "FIXO" : "VARIAVEL", linhaDoDre: null, cor: "#71717a" };

  return (
    <section className={cn(moldura, "overflow-hidden")}>
      <header className="flex items-center justify-between border-b border-zinc-100 bg-zinc-50/60 px-4 py-2.5 dark:border-border dark:bg-muted/30">
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">{tipo === "ENTRADA" ? "Categorias de entrada" : "Categorias de saída"}</h3>
        {pode && (
          <button type="button" onClick={() => setEditando(novo)} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline dark:text-primary">
            <Plus className="h-3.5 w-3.5" /> Nova categoria
          </button>
        )}
      </header>
      {editando && !editando.id && <Editor inicial={editando} comDre={comDre} onFechar={() => setEditando(null)} />}
      <ul className="divide-y divide-zinc-100 dark:divide-border/50">
        {categorias.map((c) =>
          editando?.id === c.id ? (
            <li key={c.id}>
              <Editor inicial={editando} comDre={comDre} onFechar={() => setEditando(null)} />
            </li>
          ) : (
            <li key={c.id} className={cn("flex items-center gap-3 px-4 py-2.5", !c.ativa && "opacity-50")}>
              <span className="h-3 w-3 shrink-0 rounded-[3px]" style={{ backgroundColor: c.cor || "#A1A1AA" }} />
              <span className="flex-1 text-sm text-zinc-900 dark:text-foreground">{c.nome}</span>
              {comDre && (
                <span className={cn("hidden rounded-full px-2 py-0.5 text-[10.5px] font-semibold sm:inline", c.linhaDoDre ? "bg-brand-50 text-brand-700 dark:bg-primary/10 dark:text-primary" : "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400")}>
                  {c.linhaDoDre ? LINHA_DO_DRE[c.linhaDoDre].rotulo : "Sem linha do DRE"}
                </span>
              )}
              {tipo === "SAIDA" && (!comDre || linhaTemNatureza(c.linhaDoDre)) && (
                <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10.5px] font-semibold text-zinc-600 dark:bg-muted dark:text-muted-foreground">
                  {NATUREZA_LABELS[c.natureza]}
                </span>
              )}
              {pode && c.linhaDoDre === "PERDAS_COM_CLIENTES" && <span className="w-[6.75rem] text-right text-[11px] text-zinc-400">Do sistema</span>}
              {pode && c.linhaDoDre !== "PERDAS_COM_CLIENTES" && (
                <>
                  <button type="button" aria-label={`Editar ${c.nome}`} onClick={() => setEditando({ id: c.id, nome: c.nome, tipo, natureza: c.natureza, linhaDoDre: c.linhaDoDre, cor: c.cor ?? "#71717a" })} className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-muted">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onClick={() => alternar.mutate({ id: c.id, ativa: !c.ativa })} className="w-20 text-right text-xs font-semibold text-zinc-500 hover:text-zinc-900 dark:hover:text-foreground">
                    {c.ativa ? "Desativar" : "Reativar"}
                  </button>
                </>
              )}
            </li>
          ),
        )}
      </ul>
    </section>
  );
}

/** Categorias não se apagam (lançamentos apontam para elas): desativar tira das escolhas. */
export function CategoriasFinanceiras() {
  const pode = usePermissao("finance.manage");
  const categorias = trpc.financeiro.categorias.listar.useQuery().data ?? [];
  const comDre = !!trpc.financeiro.configFinanceira.ler.useQuery().data?.dreLigado;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Grupo tipo="SAIDA" categorias={categorias.filter((c) => c.tipo === "SAIDA")} pode={pode} comDre={comDre} />
      <Grupo tipo="ENTRADA" categorias={categorias.filter((c) => c.tipo === "ENTRADA")} pode={pode} comDre={comDre} />
    </div>
  );
}
